// components/NoticeList.tsx
import React, { useEffect, useState } from 'react';
import { Chip, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { getNotices, readNotice, Notice } from '../api';
import { useNotice } from '../contexts/NoticeContext';
import { parseApiDateTime } from '../utils/apiDateTime';

const TARGET_APP_LABELS: Record<string, string> = {
  hand_hygiene_app: '手指衛生ラウンド',
  hand_hygiene_summary_app: '手指衛生データ分析アプリ',
};

const NoticeList: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { refreshUnread } = useNotice();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getNotices();
        setNotices(data);
      } catch (e) {
        console.error(e);
        setError('お知らせの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleClick = async (notice: Notice) => {
    const newExpandedId = expandedId === notice.id ? null : notice.id;
    setExpandedId(newExpandedId);

    if (!notice.is_read) {
      try {
        await readNotice(notice.id, true);
        setNotices((prev) =>
          prev.map((n) => (n.id === notice.id ? { ...n, is_read: true } : n))
        );
        await refreshUnread();
      } catch (e) {
        console.error(e);
      }
    }
  };

  if (loading) {
    return <Typography>読み込み中...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (notices.length === 0) {
    return <Typography>お知らせはありません。</Typography>;
  }

  return (
    <List>
      {notices.map((notice) => {
        const targetLabel = notice.target_app
          ? TARGET_APP_LABELS[notice.target_app] ?? notice.target_app
          : null;

        return (
          <ListItem
            key={notice.id}
            onClick={() => handleClick(notice)}
            sx={{
              bgcolor: 'action.hover',
              mb: 1,
              borderRadius: 1,
              cursor: 'pointer',
              py: 2,
              px: 2,
            }}
            alignItems="flex-start"
          >
            <ListItemText
              primary={notice.title}
              primaryTypographyProps={{
                sx: {
                  fontWeight: notice.is_read ? 'normal' : 'bold',
                  fontSize: { xs: '1.1rem', sm: '1.2rem' },
                },
              }}
              secondary={
                <>
                  <Typography
                    component="span"
                    variant="body1"
                    display="block"
                    sx={{ fontSize: { xs: '1rem', sm: '1.05rem' }, color: 'text.secondary' }}
                  >
                    {parseApiDateTime(notice.published_at)?.toLocaleString() ?? notice.published_at}
                  </Typography>
                  {expandedId === notice.id && (
                    <Typography
                      component="span"
                      display="block"
                      sx={{
                        whiteSpace: 'pre-line',
                        fontSize: { xs: '1.05rem', sm: '1.1rem' },
                        mt: 1,
                      }}
                    >
                      {notice.body}
                    </Typography>
                  )}
                  {targetLabel && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip
                        label={targetLabel}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  )}
                </>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
};

export default NoticeList;
